<template>
	<div
		class="info-bubble-container"
		:class="{ 'hover-right': effectivePosition === 'right', 'hover-left': effectivePosition === 'left', 'hover-top': effectivePosition === 'top', 'hover-bottom': effectivePosition === 'bottom' }"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<div class="info-icon" ref="iconRef">
			<img src="@/assets/icon/question.svg" alt="?" />
		</div>
		<div
			ref="measureRef"
			class="tooltip tooltip-measure"
			aria-hidden="true"
		>
			<span v-html="message"></span>
		</div>
		<Teleport to="body">
			<Transition name="tooltip-bubble">
				<div
					v-if="showTooltip"
					ref="tooltipRef"
					class="tooltip"
					:style="tooltipStyle"
					:class="{
						'tooltip-right': effectivePosition === 'right',
						'tooltip-left': effectivePosition === 'left',
						'tooltip-top': effectivePosition === 'top',
						'tooltip-bottom': effectivePosition === 'bottom',
					}"
					@mouseenter="onTooltipEnter"
					@mouseleave="onTooltipLeave"
				>
					<slot><span v-html="message"></span></slot>
				</div>
			</Transition>
		</Teleport>
	</div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
	message: {
		type: String,
		default: 'Info message',
	},
	position: {
		type: String,
		default: 'right',
		validator: value => ['top', 'right', 'bottom', 'left'].includes(value),
	},
});

const showTooltip = ref(false);
const iconRef = ref(null);
const tooltipRef = ref(null);
const measureRef = ref(null);
const tooltipStyle = ref({});
const closeTimeoutId = ref(null);
const effectivePosition = ref(props.position);

const tooltip_gap_px = 10;
const close_delay_ms = 120;
const tooltip_estimated_width = 250;
const tooltip_estimated_height = 80;

const clearCloseTimeout = () => {
	if (closeTimeoutId.value) {
		clearTimeout(closeTimeoutId.value);
		closeTimeoutId.value = null;
	}
};

const getTooltipSize = () => {
	if (tooltipRef.value) {
		return {
			width: tooltipRef.value.offsetWidth || tooltip_estimated_width,
			height: tooltipRef.value.offsetHeight || tooltip_estimated_height,
		};
	}
	if (measureRef.value) {
		return {
			width: measureRef.value.offsetWidth || tooltip_estimated_width,
			height: measureRef.value.offsetHeight || tooltip_estimated_height,
		};
	}
	return { width: tooltip_estimated_width, height: tooltip_estimated_height };
};

const positionFits = (rect, viewport, size, position) => {
	const { width: tw, height: th } = size;
	const vw = viewport.width;
	const vh = viewport.height;

	if (position === 'top') {
		const top = rect.top - tooltip_gap_px - th;
		const left = rect.left + rect.width / 2 - tw / 2;
		return top >= 0 && left >= 0 && left + tw <= vw;
	}
	if (position === 'bottom') {
		const top = rect.bottom + tooltip_gap_px;
		const left = rect.left + rect.width / 2 - tw / 2;
		return top + th <= vh && left >= 0 && left + tw <= vw;
	}
	if (position === 'right') {
		const left = rect.right + tooltip_gap_px;
		const top = rect.top + rect.height / 2 - th / 2;
		return left + tw <= vw && top >= 0 && top + th <= vh;
	}
	if (position === 'left') {
		const left = rect.left - tooltip_gap_px - tw;
		const top = rect.top + rect.height / 2 - th / 2;
		return left >= 0 && top >= 0 && top + th <= vh;
	}
	return false;
};

const getBestPosition = (rect, viewport, size) => {
	const preferred = props.position;
	const all = ['top', 'right', 'bottom', 'left'];
	const order = [preferred, ...all.filter(p => p !== preferred)];
	for (const pos of order) {
		if (positionFits(rect, viewport, size, pos)) return pos;
	}
	return preferred;
};

const applyPositionStyle = (rect, position) => {
	if (position === 'right') {
		tooltipStyle.value = {
			position: 'fixed',
			top: `${rect.top + rect.height / 2}px`,
			left: `${rect.right + tooltip_gap_px}px`,
			transform: 'translateY(-50%)',
			zIndex: 10000,
		};
		return;
	}
	if (position === 'left') {
		tooltipStyle.value = {
			position: 'fixed',
			top: `${rect.top + rect.height / 2}px`,
			left: `${rect.left - tooltip_gap_px}px`,
			transform: 'translate(-100%, -50%)',
			zIndex: 10000,
		};
		return;
	}
	if (position === 'top') {
		tooltipStyle.value = {
			position: 'fixed',
			top: `${rect.top - tooltip_gap_px}px`,
			left: `${rect.left + rect.width / 2}px`,
			transform: 'translate(-50%, -100%)',
			zIndex: 10000,
		};
		return;
	}
	tooltipStyle.value = {
		position: 'fixed',
		top: `${rect.bottom + tooltip_gap_px}px`,
		left: `${rect.left + rect.width / 2}px`,
		transform: 'translate(-50%, 0)',
		zIndex: 10000,
	};
};

const updateTooltipPosition = () => {
	if (!iconRef.value) return;
	const rect = iconRef.value.getBoundingClientRect();
	applyPositionStyle(rect, effectivePosition.value);
};

const addGlobalListeners = () => {
	window.addEventListener('resize', updateTooltipPosition);
	window.addEventListener('scroll', updateTooltipPosition, true);
};

const removeGlobalListeners = () => {
	window.removeEventListener('resize', updateTooltipPosition);
	window.removeEventListener('scroll', updateTooltipPosition, true);
};

const onMouseEnter = () => {
	clearCloseTimeout();
	addGlobalListeners();
	nextTick(() => {
		if (!iconRef.value) return;
		const rect = iconRef.value.getBoundingClientRect();
		const viewport = { width: window.innerWidth, height: window.innerHeight };
		const size = getTooltipSize();
		effectivePosition.value = getBestPosition(rect, viewport, size);
		applyPositionStyle(rect, effectivePosition.value);
		showTooltip.value = true;
	});
};

const onMouseLeave = () => {
	clearCloseTimeout();
	closeTimeoutId.value = setTimeout(() => {
		showTooltip.value = false;
		removeGlobalListeners();
		closeTimeoutId.value = null;
	}, close_delay_ms);
};

const onTooltipEnter = () => {
	clearCloseTimeout();
};

const onTooltipLeave = () => {
	onMouseLeave();
};

onBeforeUnmount(() => {
	clearCloseTimeout();
	removeGlobalListeners();
});
</script>

<style lang="scss" scoped>
.info-bubble-container {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: relative;
	padding: 6px;
	margin: -6px;
	border-radius: 999px;
}

.info-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	min-width: 20px;
	min-height: 20px;
	border-radius: 100%;
	cursor: pointer;
	user-select: none;
	font-size: 1rem;

	img {
		width: 20px;
		height: 20px;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		display: block;
	}
}

$tooltip-duration: 0.28s;
$tooltip-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);
$tooltip-ease-out: cubic-bezier(0.33, 1, 0.68, 1);

.tooltip-bubble-enter-active {
	transition:
		opacity $tooltip-duration $tooltip-ease-out,
		transform $tooltip-duration $tooltip-bounce;
}

.tooltip-bubble-leave-active {
	transition:
		opacity $tooltip-duration $tooltip-ease-out,
		transform $tooltip-duration $tooltip-ease-out;
}

.tooltip-right.tooltip-bubble-enter-from,
.tooltip-right.tooltip-bubble-leave-to {
	opacity: 0;
	transform: translateY(-50%) translateX(-14px) scale(0.94);
}
.tooltip-right.tooltip-bubble-enter-to,
.tooltip-right.tooltip-bubble-leave-from {
	opacity: 1;
	transform: translateY(-50%) translateX(0) scale(1);
}

.tooltip-left.tooltip-bubble-enter-from,
.tooltip-left.tooltip-bubble-leave-to {
	opacity: 0;
	transform: translateY(-50%) translateX(14px) scale(0.94);
}
.tooltip-left.tooltip-bubble-enter-to,
.tooltip-left.tooltip-bubble-leave-from {
	opacity: 1;
	transform: translateY(-50%) translateX(0) scale(1);
}

.tooltip-top.tooltip-bubble-enter-from,
.tooltip-top.tooltip-bubble-leave-to {
	opacity: 0;
	transform: translateX(-50%) translateY(10px) scale(0.94);
}
.tooltip-top.tooltip-bubble-enter-to,
.tooltip-top.tooltip-bubble-leave-from {
	opacity: 1;
	transform: translateX(-50%) translateY(0) scale(1);
}

.tooltip-bottom.tooltip-bubble-enter-from,
.tooltip-bottom.tooltip-bubble-leave-to {
	opacity: 0;
	transform: translateX(-50%) translateY(-10px) scale(0.94);
}
.tooltip-bottom.tooltip-bubble-enter-to,
.tooltip-bottom.tooltip-bubble-leave-from {
	opacity: 1;
	transform: translateX(-50%) translateY(0) scale(1);
}

.tooltip {
	background-color: var(--beige-400);
	color: var(--gris-700);
	padding: .5rem .8rem;
	border-radius: 8px;
	border: 1px solid var(--beige-600);
	font-size: 0.9rem;
	width: 250px;
	z-index: 999;
	white-space: normal;
	line-height: 1.4;
	letter-spacing: 0.2px;
	text-align: left;
	transform-origin: var(--tooltip-origin-x, left) var(--tooltip-origin-y, center);

	&::before,
	&::after {
		content: '';
		position: absolute;
		width: 0;
		height: 0;
	}

	&::before {
		border: 9px solid transparent;
		z-index: 1;
	}

	&::after {
		border: 9px solid transparent;
		z-index: 0;
	}
}

.tooltip-measure {
	position: fixed;
	left: -9999px;
	top: 0;
	visibility: hidden;
	pointer-events: none;
}

.tooltip-right {
	--tooltip-origin-x: left;
	--tooltip-origin-y: center;

	&::after {
		right: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-right: 1px;
		border-right-color: var(--beige-600);
	}

	&::before {
		right: 100%;
		top: 50%;
		transform: translateY(-50%);
		border-right-color: var(--beige-400);
	}
}

.tooltip-left {
	--tooltip-origin-x: right;
	--tooltip-origin-y: center;

	&::after {
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 1px;
		border-left-color: var(--beige-600);
	}

	&::before {
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		border-left-color: var(--beige-400);
	}
}

.tooltip-top {
	--tooltip-origin-x: center;
	--tooltip-origin-y: bottom;

	&::after {
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		margin-top: 1px;
		border-top-color: var(--beige-600);
	}

	&::before {
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border-top-color: var(--beige-400);
	}
}

.tooltip-bottom {
	--tooltip-origin-x: center;
	--tooltip-origin-y: top;

	&::after {
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		margin-bottom: 1px;
		border-bottom-color: var(--beige-600);
	}

	&::before {
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		border-bottom-color: var(--beige-400);
	}
}
</style>
